import { useMediaQuery } from '@mantine/hooks'
import { createBrowserHistory } from 'history'
import { type ReactNode, useContext, useEffect, useLayoutEffect } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import { useClient } from '../../App/ClientProvider'
import { ReportFormContext } from '../../Contexts/ReportFormContext'
import { breakpoints, desktopBreakpoint, type LayoutVariant } from '../../utils/constants'
import { resolveIsBrandingHidden } from '../../utils/resolveIsBrandingHidden'
import useReportAuth from '../../utils/useReportAuth'
import { AppWrapper } from './AppWrapper'
import { AppFooter, Header, Navbar, WebFooter } from './components'
import { useMenuOpen } from './hooks/useMenuOpen'

/**
 * The layout's slice of the report-source init query. It composes the fragments
 * of the components InstitutionLayout renders (Header/Navbar/WebFooter/AppFooter)
 * plus the branding config it resolves itself - a layout may bundle its own
 * children's fragments, the report-form provider may not.
 */
const fragments = {
  InstitutionLayout_reportSource: graphql(`
    fragment InstitutionLayout_reportSource on PublicReportSource {
      id
      institution {
        id
        config {
          id
          isBrandingHidden
        }
        partner {
          id
          config {
            id
            isBrandingHidden
          }
        }
      }
      ...Header_reportSource
      ...InstitutionLayoutNavbar_reportSource
      ...InstitutionLayoutWebFooter_reportSource
      ...AppFooter_reportSource
    }
  `),
}

type InstitutionLayoutProps = {
  children?: ReactNode
  reportSource: FragmentType<typeof fragments.InstitutionLayout_reportSource>
  variant?: LayoutVariant
  topAligned?: boolean
}
export const InstitutionLayout = ({
  children,
  reportSource: _reportSource,
  variant = 'complex',
  topAligned,
}: InstitutionLayoutProps) => {
  const reportSource = getFragmentData(fragments.InstitutionLayout_reportSource, _reportSource)
  const { isAuthenticated, logout: reportLogout } = useReportAuth()
  const { client } = useClient()
  const { isAnonymous } = useContext(ReportFormContext)

  const [opened, setOpened] = useMenuOpen()

  const history = createBrowserHistory()

  const isApp = client === 'app'
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints[desktopBreakpoint]})`)

  useEffect(() => {
    if (isDesktop && opened) {
      setOpened(false)
    }
  }, [isDesktop, opened, setOpened])

  useLayoutEffect(() => {
    history.listen(() => {
      if (
        isAuthenticated() &&
        client !== 'app' &&
        ((history.action === 'PUSH' && ['/create', '/'].includes(history.location.pathname)) ||
          (history.action === 'REPLACE' && history.location.pathname === '/check'))
      ) {
        reportLogout()
        window.location.href = '/'
      }
    })
  }, [history, isAuthenticated, reportLogout, client])

  const isBrandingHidden = resolveIsBrandingHidden(reportSource.institution)

  return (
    <AppWrapper
      variant={variant}
      topAligned={topAligned}
      headerComponent={
        <Header
          isMenuOpened={opened}
          setIsMenuOpened={setOpened}
          reportSource={reportSource}
          variant={variant}
        />
      }
      navbarComponent={<Navbar reportSource={reportSource} isOpened={opened} />}
      footerComponent={
        isApp ? (
          <AppFooter reportSource={reportSource} />
        ) : isDesktop || variant === 'simple' ? undefined : (
          <WebFooter reportSource={reportSource} shouldShowLanguagesDropdown={opened} />
        )
      }
      isBrandingHidden={isBrandingHidden}
      isAnonymous={isAnonymous}
    >
      {children}
    </AppWrapper>
  )
}
